import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    Renderer2,
    SimpleChanges
} from '@angular/core';
import { IProductMediaItem as CoreIProductMediaItem, IProductDataV3 } from '@df/catalog/product/product';
import { Client } from '@df/core/service/client';
import { AbService } from '@df/module-ab/ab.service';
import { ImageModule } from '@df/module-image/image.module';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { filterMediaByLabel } from '@df/ui/common/filter-media-by-label-pipe';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { PhotoswipeLightboxComponent } from '@df/ui/photoswipe/photoswipe-lightbox.component';
import { PhotoSwipeModule } from '@df/ui/photoswipe/photoswipe.module';
import { PhotoswipeUiDefaultOptions } from '@df/ui/photoswipe/photoswipe.types';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import isEqual from 'lodash-es/isEqual';
import take from 'lodash-es/take';
import uniqBy from 'lodash-es/uniqBy';
import { Swiper } from 'swiper';
import { SwiperEvents } from 'swiper/types';
import { ShellComponent } from '../../../ui/display/local-shell.component';
import { EListingLabelOptionId, IProductLabel, Product } from '../product.decorator';
import { ProductService } from '../product.service';
let uid = 0;

declare module '@df/catalog/product/product';
export interface IProductMediaItem extends CoreIProductMediaItem {
    alt_text?: string;
}

@Component({
    selector: 'product-view-media',
    templateUrl: 'product-view-media.html',
    styleUrls: ['./product-view-media.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CarouselModule,
        ShellComponent,
        GridModule,
        ImageModule,
        PhotoSwipeModule,
        TranslationModule,
        CommonModule,
        VimeoPlayerComponent,
        ShellComponent,
        PhotoswipeLightboxComponent,
        IfSizeDirective
    ]
})
export class ProductViewMediaComponent extends UiComponent implements OnChanges, OnInit {
    labels: IProductLabel[] = [];
    /**
     * Source product
     */
    @Input()
    source?: Product<IProductDataV3>;
    @Input()
    isUpsellPreviewVariant = false;
    /**
     * Visible image
     */
    media?: IProductMediaItem[];
    carousel?: Swiper;
    photoswipeOptions?: Partial<PhotoswipeUiDefaultOptions>;
    uid = `product-media-${uid++}`;

    constructor(
        override readonly client: Client,
        private renderer2: Renderer2,
        private elementRef: ElementRef,
        private productService: ProductService,
        private ab: AbService
    ) {
        super();
    }

    override ngOnInit() {
        super.ngOnInit();
        this.photoswipeOptions = {
            showAnimationDuration: 250,
            hideAnimationDuration: 250,
            pinchToClose: false,
            closeOnScroll: false,
            closeOnVerticalDrag: false,
            tapToToggleControls: false,
            counterEl: false,
            barsSize: { top: 0, bottom: 'auto' },
            initialZoom: 1,
            followMouse: true,
            canPreload: false,
            getDoubleTapZoom: (isMouseClick, item: any) => {
                if (!item.zoomLevel) {
                    item.zoomLevel = item.initialZoomLevel;
                }
                let res: number;
                if (item.zoomLevel < 0.5 && (this.client.isM || this.client.isS)) {
                    res = 0.5;
                    this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--zoom-50');
                    this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--zoom-100');
                } else if (item.zoomLevel < 1) {
                    res = 1;
                    this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--zoom-100');
                    this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--zoom-50');
                } else {
                    res = item.initialZoomLevel;
                    this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--zoom-50');
                    this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--zoom-100');
                }
                item.zoomLevel = res;
                return res;
            }
        };
        this.ab.getSubject('pdpRedesign').subscribe(() => {
            this.detectChanges();
        });
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes['source']) {
            this.labels = take(this.source?.getListingLabels(), 2);
        }
    }
    get inStockLabel(): IProductLabel | undefined {
        const inStockLabel = this.productService.getListingLabelProductLabel(EListingLabelOptionId.inStock);
        return this.labels.find(label => label.label === inStockLabel?.label);
    }

    showVideoControlPanel(item: IProductMediaItem): boolean {
        return !!item.labels?.includes('show_videocontrolpanel');
    }

    onPhotoswipeStateChange(state: boolean) {
        state
            ? this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--active')
            : this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--active');
        this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--inactive');
        this.timeoutService.setTimeout(() => {
            this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--inactive');
        }, 500);
    }
    /**
     * Update media to show
     */
    override detectChanges() {
        if (this.initiated) {
            const simpleMedia = this.source?.data?.media || [];
            const parentMedia =
                (this.source?.isSimple && this.source.parent?.isConfigurable
                    ? (<Product<IProductDataV3>>this.source.parent).data?.media
                    : []) || [];
            const media = uniqBy([...this.getClearanceMedia(), ...simpleMedia, ...parentMedia], item => item.image);
            if (!isEqual(this.media, media)) {
                this.media = media;
                this.slideTo(0);
                super.detectChanges();
            }

            this.media &&
                (this.media = filterMediaByLabel(
                    this.media,
                    ['image_for_product_range_block_', 'image_for_product_range_block_2', 'image_for_product_range_block_3', 'dimensions'],
                    true
                ));
        }
    }
    private getClearanceMedia(): IProductMediaItem[] {
        if (this.source?.isClearance) {
            const parent = this.source.clearanceParent;
            if (parent?.data?.image) {
                return [{ image: parent.data.image, position: 0 }];
            }
        }
        return [];
    }
    setCarousel(eventParams: Parameters<SwiperEvents['afterInit']>): void {
        this.carousel = eventParams[0];
        this.detectChanges();
    }
    slideTo(index: number): void {
        if (this.carousel) {
            this.carousel.slideTo(index);
        }
    }
    getImageAltText(altText?: string): string {
        if (altText) return altText;
        else return this.source?.data?.name + ' ' + this.source?.data?.web_finish;
    }
}
